import { buildingData, calcResourceGatherTime, calcBuildingBuildTime } from '../../data';
import { StateFastForward } from '../core';
import { makeTimelineEntry, makeInvalidEntry } from '../timelineEntries';
import { captureStateSnapshot } from '../snapshot';
import { waitWhile, drainShorterThan } from '../waits';

export const handleUpgradeBuilding = (state, timeline, recorder, action, index) => {
  const { payload } = action;
  const { planetId, building } = payload;

  // Own build slot must be free first.
  state = waitWhile(state, timeline, recorder, planetId, (s) => s.planets[planetId].buildAction !== undefined);

  // Hangar can't be upgraded while this planet's ship queue is draining.
  if (building === 'hangar') {
    state = waitWhile(state, timeline, recorder, planetId, (s) => s.planets[planetId].shipActions.length !== 0, {
      captureSnapshots: true,
    });
  }

  // Fix vs. legacy (plan.md §4/§9): a researchLab upgrade must also wait
  // for any in-progress research anywhere in the empire, mirroring the
  // reverse rule (research waits for researchLab construction) that the
  // legacy code already enforced. Legacy only had the one direction.
  if (building === 'researchLab') {
    state = waitWhile(state, timeline, recorder, planetId, (s) => s.researchAction !== undefined, {
      captureSnapshots: true,
    });
  }

  const currentLevel = state.planets[planetId].buildings[building];
  const cost = buildingData[building].cost(currentLevel + 1);
  let gatherTime = calcResourceGatherTime(state, planetId, { ...cost });
  if (gatherTime < 0) {
    timeline.push(
      makeInvalidEntry(state, planetId, index, `Start ${buildingData[building].name} ${state.planets[planetId].buildings[building]}`)
    );
    return state;
  }

  // Snapshot is taken here, before draining shorter concurrent actions —
  // matches legacy exactly (the other three handlers capture *after* the
  // drain loop instead; see plan.md §4).
  const beforeGatherState = captureStateSnapshot(state, planetId);

  const drained = drainShorterThan(state, timeline, recorder, planetId, gatherTime, (s) =>
    calcResourceGatherTime(s, planetId, { ...cost })
  );
  state = drained.state;
  gatherTime = drained.threshold;

  const available = buildingData[building].available(state, planetId);
  if (!available) {
    timeline.push(
      makeInvalidEntry(state, planetId, index, `Start ${buildingData[building].name} ${state.planets[planetId].buildings[building]}`)
    );
    return state;
  }

  if (gatherTime > 0) {
    timeline.push(makeTimelineEntry('gather', state, { ...action, duration: gatherTime, index }, beforeGatherState));
    state = StateFastForward(state, planetId, gatherTime, recorder);
    timeline[timeline.length - 1].stateAfter = captureStateSnapshot(state, planetId);
  }

  const beforeBuildState = captureStateSnapshot(state, planetId);
  const planet = state.planets[planetId];
  const duration = calcBuildingBuildTime(state, planetId, { ...cost });
  planet.resources.metal -= cost.metal;
  planet.resources.crystal -= cost.crystal;
  planet.resources.deuterium -= cost.deuterium;
  planet.buildAction = { ...action, duration, index };
  state.planets[planetId] = planet;

  recorder.record(state, planetId, `Started ${buildingData[building].name} level ${state.planets[planetId].buildings[building] + 1}`);

  timeline.push(makeTimelineEntry('start', state, planet.buildAction, beforeBuildState, captureStateSnapshot(state, planetId)));

  return state;
};
